import { useEffect, useRef, useState, useCallback } from "react";
import { authAPI, paymentsAPI } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";
import { Logo } from "@/components/shared/Logo";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Extract session_id from every possible URL location.
 * Some mobile browsers lose the hash fragment during redirects,
 * or convert it to a query param, or encode it differently.
 */
function extractSessionId() {
  // 1. Check hash fragment: #session_id=xxx
  if (window.location.hash) {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const fromHash = hashParams.get("session_id");
    if (fromHash) return { sessionId: fromHash, source: "hash" };
  }

  // 2. Check query params: ?session_id=xxx
  const queryParams = new URLSearchParams(window.location.search);
  const fromQuery = queryParams.get("session_id");
  if (fromQuery) return { sessionId: fromQuery, source: "query" };

  // 3. Check full URL with regex (catches edge cases like double-encoded hashes)
  const fullUrl = window.location.href;
  const match = fullUrl.match(/[#?&]session_id=([^&#]+)/);
  if (match) return { sessionId: decodeURIComponent(match[1]), source: "regex" };

  return { sessionId: null, source: "none" };
}

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const { t } = useTranslation();
  const [status, setStatus] = useState("loading"); // loading | error | retrying
  const [errorMsg, setErrorMsg] = useState("");

  const processAuth = useCallback(async () => {
    const debugInfo = {
      href: window.location.href,
      hash: window.location.hash,
      search: window.location.search,
      pathname: window.location.pathname,
      timestamp: new Date().toISOString(),
    };

    // Send diagnostic info to backend (fire-and-forget)
    try {
      axios.post(`${API_BASE}/api/auth/debug-callback`, debugInfo).catch((e) => console.error("Debug callback failed:", e.message));
    } catch (e) { console.error("Debug post error:", e.message); }

    // CASE 1: Returning from preview URL with token (custom domain flow)
    // When user is on staffera.eu, the OAuth callback goes to the preview URL,
    // which then redirects back to staffera.eu/auth/callback?token=xxx&redirect=/candidate
    const queryParams = new URLSearchParams(window.location.search);
    const passedToken = queryParams.get("token");
    const passedRedirect = queryParams.get("redirect");
    if (passedToken && passedRedirect) {
      localStorage.setItem("session_token", passedToken);
      window.location.replace(passedRedirect);
      return;
    }

    // CASE 2: Normal OAuth callback with session_id in hash/query
    // Try to extract session_id with multiple strategies
    let { sessionId, source } = extractSessionId();

    // If no session_id found, wait briefly and retry
    // (some mobile browsers populate hash asynchronously)
    if (!sessionId) {
      await new Promise(r => setTimeout(r, 500));
      ({ sessionId, source } = extractSessionId());
    }
    if (!sessionId) {
      await new Promise(r => setTimeout(r, 1000));
      ({ sessionId, source } = extractSessionId());
    }

    if (!sessionId) {
      console.error("AuthCallback: No session_id found", debugInfo);
      setStatus("error");
      setErrorMsg(t("auth.error_no_session"));
      return;
    }

    if (process.env.NODE_ENV === 'development') console.log(`AuthCallback: session_id found via ${source}`);

    const savedRole = localStorage.getItem("staffera_auth_role") || "candidate";
    const savedPlan = localStorage.getItem("staffera_auth_plan");
    const privacyAccepted = localStorage.getItem("staffera_privacy_accepted") === "true";
    localStorage.removeItem("staffera_auth_role");
    localStorage.removeItem("staffera_auth_plan");
    localStorage.removeItem("staffera_privacy_accepted");

    // Exchange session_id for a session_token with retry
    let userData = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        if (attempt > 0) setStatus("retrying");
        const res = await authAPI.exchangeSession(sessionId, savedRole);
        userData = res.data;
        break;
      } catch (exchangeErr) {
        console.error(`AuthCallback: exchangeSession attempt ${attempt + 1} failed`, exchangeErr);
        if (attempt < 2) await new Promise(r => setTimeout(r, 1500));
      }
    }

    if (!userData) {
      setStatus("error");
      setErrorMsg(t("auth.error_session_exchange"));
      return;
    }

    // Store session token in localStorage (critical for mobile where cookies may fail)
    if (userData.session_token) {
      localStorage.setItem("session_token", userData.session_token);
    }

    // Accept privacy policy for OAuth users
    if (privacyAccepted) {
      try { await authAPI.acceptPrivacy(); } catch (e) { console.error("acceptPrivacy failed:", e.message); }
    }

    // Handle paid plan redirect
    if (savedPlan && userData.role === "hotel") {
      try {
        const checkout = await paymentsAPI.createCheckout({ plan: savedPlan, payment_type: "subscription" });
        window.location.href = checkout.data.url;
        return;
      } catch (e) { console.error("Checkout redirect failed:", e.message); }
    }

    // Determine redirect path
    const path = userData.role === "admin" ? "/admin" : userData.role === "hotel" ? "/hotel" : "/candidate";
    if (userData.role === "candidate" && !userData.subscription_status) {
      sessionStorage.setItem("staffera_welcome", "1");
    }

    // Redirect back to the user's original domain (e.g. staffera.eu)
    // if they came from a custom domain. The session_token is already
    // stored in localStorage on THIS origin (preview URL), so we also
    // need to pass it to the custom domain via a query param.
    const returnOrigin = localStorage.getItem("staffera_return_origin");
    localStorage.removeItem("staffera_return_origin");

    if (returnOrigin && returnOrigin !== window.location.origin) {
      // Redirect to the custom domain with session_token so it can
      // store it in its own localStorage
      const token = localStorage.getItem("session_token") || "";
      window.location.replace(`${returnOrigin}/auth/callback?token=${encodeURIComponent(token)}&redirect=${encodeURIComponent(path)}`);
    } else {
      // Same origin - simple redirect
      window.location.replace(path);
    }
  }, [t]);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    processAuth().catch((err) => {
      console.error("Auth callback fatal error:", err);
      setStatus("error");
      setErrorMsg(t("auth.error_generic"));
    });
  }, [processAuth, t]);

  const handleRetry = () => {
    // Retry the entire flow
    setStatus("loading");
    setErrorMsg("");
    hasProcessed.current = false;
    processAuth().catch(() => {
      setStatus("error");
      setErrorMsg(t("auth.error_generic"));
    });
  };

  const handleBackToLogin = () => {
    window.location.replace("/auth");
  };

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center px-6 max-w-sm">
        <Logo size="lg" className="text-white justify-center mb-6" />

        {status === "loading" && (
          <div className="space-y-4">
            <div className="w-8 h-8 border-2 border-sky-400 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-white font-body">{t("auth.signing_in")}</p>
          </div>
        )}

        {status === "retrying" && (
          <div className="space-y-4">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-amber-300 font-body">{t("auth.retrying")}</p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <p className="text-red-400 font-body text-sm">{errorMsg}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleRetry}
                className="w-full py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-body text-sm transition-colors"
                data-testid="auth-retry-btn"
              >
                {t("auth.retry")}
              </button>
              <button
                onClick={handleBackToLogin}
                className="w-full py-2.5 rounded-lg border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 font-body text-sm transition-colors"
                data-testid="auth-back-login-btn"
              >
                {t("auth.back_to_login")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
